import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import t from "@/lang/lang";
import { TableLoadingRow } from "@/shared/components/tables/table-loading-row";
import { TableRtkErrorRow } from "@/shared/components/tables/table-rtk-error-row";
import { TableEmptyRow } from "@/shared/components/tables/table-empty-row";
import { CustomerName } from "@/services/backend/customers/components/customer-name";
import { ServiceObjectName } from "@/services/backend/customers/components/service-object-name";
import { CompanyName } from "@/services/backend/company/components/company-name";
import { formatDateTime } from "@/shared/lib/utilities/date";
import { SearchFilter } from "@/shared/components/filter/search-filter";
import { CustomerFilter } from "@/services/backend/customers/components/customer-filter";
import { ServiceObjectFilter } from "@/services/backend/customers/components/service-object-filter";
import { CompanyFilter } from "@/services/backend/company/components/company-filter";
import { OrderBySelect } from "@/shared/components/tables/order-by-select";
import { ReactNode } from "react";
import { AirHandlingUnitData } from "../types/air-handling-unit-data";
import {
  AhuDataOrderBy,
  ListRequest,
  useHtzAirHandlingUnitDataListQuery,
} from "@/services/backend/htz/ahu/api/ahu-data-api";

export function AhuDataTable({
  request,
  renderRowLink,
}: {
  request: ListRequest;
  renderRowLink: (ahuDataId: string, children: ReactNode) => ReactNode;
}) {
  const { data, isLoading, error } =
    useHtzAirHandlingUnitDataListQuery(request);

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{t("Unternehmen")}</TableHead>
          <TableHead>{t("Kunde")}</TableHead>
          <TableHead>{t("Objekt")}</TableHead>
          <TableHead>{t("Name")}</TableHead>
          <TableHead>{t("Label")}</TableHead>
          <TableHead>{t("Erstellt am")}</TableHead>
          <TableHead>{t("Aktualisiert am")}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {isLoading && <TableLoadingRow colSpan={7} />}
        {error && <TableRtkErrorRow error={error} colSpan={7} />}
        {data && data.items.length === 0 && <TableEmptyRow colSpan={7} />}
        {data &&
          data.items.map((ahuData) => (
            <AhuDataTableRow
              key={ahuData.id}
              ahuData={ahuData}
              renderRowLink={renderRowLink}
            />
          ))}
      </TableBody>
    </Table>
  );
}

function AhuDataTableRow({
  ahuData,
  renderRowLink,
}: {
  ahuData: AirHandlingUnitData;
  renderRowLink: (ahuDataId: string, children: ReactNode) => ReactNode;
}) {
  return (
    <TableRow>
      <TableCell>
        <CompanyName companyId={ahuData.companyId} short />
      </TableCell>
      <TableCell>
        <CustomerName customerId={ahuData.customerId} />
      </TableCell>
      <TableCell>
        <ServiceObjectName id={ahuData.serviceObjectId} />
      </TableCell>
      <TableCell>
        {renderRowLink(ahuData.id, <>{ahuData.designation}</>)}
      </TableCell>
      <TableCell>{ahuData.label}</TableCell>
      <TableCell>{formatDateTime(ahuData.createdAt)}</TableCell>
      <TableCell>{formatDateTime(ahuData.updatedAt)}</TableCell>
    </TableRow>
  );
}

export function AhuDataTableFilters({
  request,
  onRequestChange,
  hideCompanyFilter,
  hideCustomerFilter,
  hideServiceObjectFilter,
}: {
  request: ListRequest;
  onRequestChange: (r: ListRequest) => void;
  hideCompanyFilter?: boolean;
  hideCustomerFilter?: boolean;
  hideServiceObjectFilter?: boolean;
}) {
  return (
    <div className="flex items-center gap-2">
      <SearchFilter
        filter={request.label || { active: false, values: [] }}
        onFilterChange={(label) => onRequestChange({ ...request, label })}
        placeholder={t("Nach Label suchen ...")}
        className="h-8"
      />
      <CompanyFilter
        filter={request.company ?? { active: false, values: [] }}
        onFilterChange={(company) => onRequestChange({ ...request, company })}
        hidden={hideCompanyFilter}
      />
      <CustomerFilter
        filter={request.customer ?? { active: false, values: [] }}
        onFilterChange={(customer) => onRequestChange({ ...request, customer })}
        hidden={hideCustomerFilter}
      />
      <ServiceObjectFilter
        filter={request.serviceObject ?? { active: false, values: [] }}
        onFilterChange={(serviceObject) =>
          onRequestChange({ ...request, serviceObject })
        }
        customerIds={request.customer?.values}
        hidden={hideServiceObjectFilter}
      />
      <OrderBySelect<AhuDataOrderBy>
        orderBy={request.orderBy}
        onOrderByChange={(orderBy) => onRequestChange({ ...request, orderBy })}
        fields={[
          { label: t("Name"), value: "designation" },
          { label: t("Label"), value: "label" },
          { label: t("Standort"), value: "location" },
          { label: t("Konstruktions Jahr"), value: "constructionYear" },
          { label: t("Volumenstrom"), value: "volumeFlowRate" },
          { label: t("Erstellt am"), value: "createdAt" },
          { label: t("Aktualisiert am"), value: "updatedAt" },
        ]}
      />
    </div>
  );
}
